import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  PencilIcon,
  PrinterIcon,
  DocumentDuplicateIcon, // Add icon for appendix print
  ClipboardDocumentListIcon,
  ArrowLeftIcon,
  BanknotesIcon,
  CalendarDaysIcon,
  InformationCircleIcon,
  UserCircleIcon,
  BuildingOffice2Icon,
  CurrencyDollarIcon,
  PlusCircleIcon,
  ListBulletIcon,
  TrashIcon,
  UserIcon
} from '@heroicons/react/24/outline';
import axios from '../../api/axiosConfig';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

interface Structure {
  id: number;
  code: string;
  type: string;
  location: string;
  isLED?: boolean;
}

interface OrderItem {
  id: number;
  structureId: number;
  startDate: string;
  endDate: string;
  unitPrice: number | null;
  isMonthlyRate: boolean;
  discountPercentage: number | null;
  totalPrice: number;
  finalPrice: number;
  notes?: string;
  status?: string;
  Structure: Structure;
  quantity: number;
}

interface Client {
  id: number;
  name: string;
  director?: string;
  phone?: string;
  email?: string;
  address?: string;
}

interface Payment {
  id: number;
  amount: number;
  date: string;
  method: string;
  referenceNumber?: string;
}

interface CreatedByUser {
  id: number;
  username: string;
  name?: string;
  firstName?: string;
  lastName?: string;
}

interface Order {
  id: number;
  orderNumber: string;
  clientId: number;
  status: 'reserved' | 'waitingPayment' | 'partiallyPaid' | 'fullyPaid' | 'completed' | 'cancelled';
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  paidAmount: number;
  orderDate: string;
  startDate: string;
  endDate: string;
  paymentTerms?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  contractId?: number | null;
  Client: Client;
  OrderItems: OrderItem[];
  Payments?: Payment[];
  CreatedBy?: CreatedByUser;
}

const ORDER_STATUS_MAP: { [key: string]: string } = {
  reserved: 'Зарезервирован',
  waitingPayment: 'Ожидает оплаты',
  partiallyPaid: 'Частично оплачен',
  fullyPaid: 'Оплачен',
  completed: 'Завершен',
  cancelled: 'Отменен'
};

const getStatusText = (status: string): string => {
  return ORDER_STATUS_MAP[status] || status;
};

const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'reserved': return 'bg-purple-100 text-purple-800';
    case 'waitingPayment': return 'bg-yellow-100 text-yellow-800';
    case 'partiallyPaid': return 'bg-blue-100 text-blue-800';
    case 'fullyPaid': return 'bg-green-100 text-green-800';
    case 'completed': return 'bg-gray-100 text-gray-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const OrderDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, can } = useAuth(); // Changed isAllowed to can
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const canEdit = can('orders.edit'); // Changed from isAllowed
  const canManagePayments = can('payments.manage'); // Changed from isAllowed, assuming general payment management for order
  const canManageWorkOrders = can('workOrders.create_from_order'); // Changed from isAllowed, specific to creating from order
  const canView = can('orders.view'); // Changed from isAllowed
  const canDelete = can('orders.delete'); // Changed from isAllowed

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!canView) {
          setError('У вас нет прав для просмотра этой страницы.');
          setLoading(false);
          return;
      }
      setLoading(true);
      try {
        const response = await axios.get(`/orders/${id}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Error fetching order details:', err);
        setError('Ошибка при загрузке данных заказа');
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id, canView]);

  // Open printable view in a new tab
  const handleOpenPrintView = () => {
    if (!order) return;
    setGeneratingPdf(true); // Keep using existing state for loading indication
    // Construct the URL for the print page
    const printUrl = `/orders/${order.id}/print`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
    // Reset loading state after a short delay
    setTimeout(() => setGeneratingPdf(false), 1000);
  };

  // Open printable appendix view in a new tab
  const handleOpenAppendixPrintView = () => {
    if (!order) return;
    // Construct the URL for the appendix print page
    const printUrl = `/orders/${order.id}/appendix/print`;
    // Open the URL in a new tab
    const printWindow = window.open(printUrl, '_blank');
    if (!printWindow) {
      alert('Не удалось открыть окно для печати приложения. Пожалуйста, проверьте настройки блокировки всплывающих окон.');
    }
    // No separate loading state needed for this button for now
  };

  const handleCreateWorkOrder = () => {
    if (!canManageWorkOrders) return;
    navigate(`/work-orders/new?orderId=${id}`);
  };

  const handleAddPayment = () => {
    if (!canManagePayments) return;
    navigate(`/payments/new?orderId=${id}`);
  };
  
  const handleUpdateStatus = async (newStatus: keyof typeof ORDER_STATUS_MAP) => {
    if (!order || !canEdit) return;
    setUpdatingStatus(true);
    try {
      await axios.patch(`/orders/${id}/status`, { status: newStatus });
      const response = await axios.get(`/orders/${id}`);
      setOrder(response.data);
    } catch (err: any) {
      console.error('Error updating order status:', err);
      alert(err.response?.data?.message || 'Ошибка при обновлении статуса заказа');
    } finally {
      setUpdatingStatus(false);
    }
  };
  
  const handleDelete = async () => {
    setShowDeleteModal(false);
    if (!order) return;
    try {
      await axios.delete(`/orders/${order.id}`);
      navigate('/orders');
    } catch (err: any) {
      console.error('Error deleting order:', err);
      setError(err.response?.data?.message || 'Не удалось удалить заказ.');
    }
  };
  
  const formatDate = (dateString: string | undefined | null) => {
    if (!dateString) return '-';
    try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
            return 'Неверная дата';
        }
        return format(date, 'dd MMMM yyyy', { locale: ru });
    } catch (e) {
        console.error("Error formatting date:", dateString, e);
        return 'Ошибка даты';
    }
  };
  
  const formatCurrency = (amount: number | undefined | null) => {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const calculateDuration = (startDate: string, endDate: string): number | null => {
     if (!startDate || !endDate) return null;
     try {
        const start = new Date(startDate);
        const end = new Date(endDate);
        if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) {
            return null;
        }
        const diffTime = end.getTime() - start.getTime();
        const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1;
        return diffDays;
     } catch (e) {
         console.error("Error calculating duration:", startDate, endDate, e);
         return null;
     }
  };
  
  const formatDuration = (startDate: string, endDate: string, isMonthlyRate: boolean, quantity: number): string => {
      if (isMonthlyRate === null) {
        return `${quantity} ед.`;
      }
      
      if (isMonthlyRate) {
        return `${quantity} мес.`;
      }
      
      const duration = calculateDuration(startDate, endDate);
      return duration !== null ? `${duration} дн.` : '-';
  };

  const calculatePaymentProgress = () => {
    if (!order || !order.finalAmount || order.finalAmount === 0) return 0;
    return Math.min(100, Math.round((order.paidAmount / order.finalAmount) * 100));
  };
  
  const formatPaymentProgress = () => {
    return `${calculatePaymentProgress()}%`;
  };
  
  if (loading) {
    return <LoadingSpinner />;
  }
  
  if (error) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-red-600">{error}</p>
        <Link to="/orders" className="mt-4 btn-secondary inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к списку
        </Link>
      </div>
    );
  }
  
  if (!order) {
    return (
      <div className="bg-white rounded-lg shadow p-6 text-center">
        <p className="text-gray-600">Заказ не найден</p>
        <Link to="/orders" className="mt-4 btn-secondary inline-flex items-center">
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Вернуться к списку
        </Link>
      </div>
    );
  }
  
  const balanceDue = order.finalAmount - order.paidAmount;

  return (
    <div>
       <PageHeader 
        title={`Заказ #${order.orderNumber}`}
        subtitle={`Детали заказа от ${formatDate(order.orderDate)}`}
        backLink="/orders"
        actions={
          <div className="flex space-x-2">
            {/* Add py-1 px-2 text-xs to make buttons smaller */}
            {canEdit && (
              <Link to={`/orders/edit/${order.id}`} className="btn-secondary flex items-center py-1 px-2 text-xs">
                <PencilIcon className="h-4 w-4 mr-1" /> Редактировать {/* Adjust icon size */}
              </Link>
            )}

            <Link 
              to={`/orders/new?copyFrom=${order.id}`}
              className="btn-secondary flex items-center py-1 px-2 text-xs"
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-1" /> Копировать
            </Link>
            
            {canDelete && (
              <button onClick={() => setShowDeleteModal(true)} className="btn-danger flex items-center py-1 px-2 text-xs">
                <TrashIcon className="h-4 w-4 mr-1" /> Удалить {/* Adjust icon size */}
              </button>
            )}
            <button
              onClick={handleOpenPrintView} // Use new handler
              className={`btn-secondary inline-flex items-center py-1 px-2 text-xs ${generatingPdf ? 'opacity-50 cursor-not-allowed' : ''}`}
              disabled={generatingPdf}
            >
              <PrinterIcon className="h-4 w-4 mr-1" /> {/* Adjust icon size */}
              {generatingPdf ? 'Открытие...' : 'Печать Счета'}
            </button>
            {/* New Button for Appendix Print */}
            <button
              onClick={handleOpenAppendixPrintView}
              className="btn-secondary inline-flex items-center py-1 px-2 text-xs"
              title="Печать Приложения №1 к договору" // Add tooltip
            >
              <DocumentDuplicateIcon className="h-4 w-4 mr-1" /> {/* Adjust icon size */}
              Печать Приложения
            </button>
            {canManageWorkOrders && (
                <button
                    onClick={handleCreateWorkOrder}
                    className="btn-secondary inline-flex items-center py-1 px-2 text-xs"
                >
                  <ClipboardDocumentListIcon className="h-4 w-4 mr-1" /> {/* Adjust icon size */}
                  Создать Наряд-Заказ
                </button>
            )}
            {canManagePayments && (
                <button
                    onClick={handleAddPayment}
                    className="btn-secondary inline-flex items-center py-1 px-2 text-xs"
                >
                    <BanknotesIcon className="h-5 w-5 mr-2" />
                    Добавить Оплату
                </button>
            )}
          </div>
        }
      />
      
      {error && <div className="text-red-600 bg-red-100 p-4 rounded-md mb-6">{error}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
                 <h2 className="text-xl font-bold mb-4 flex items-center">
                     <UserCircleIcon className="h-6 w-6 mr-2 text-primary"/>
                     Клиент
                 </h2>
                 {order.Client ? (
                     <div className="text-sm">
                         <p className="font-medium text-gray-900">
                             <Link to={`/clients/${order.clientId}`} className="text-primary hover:underline">
                                 {order.Client.name}
                             </Link>
                         </p>
                         {order.Client.director && <p className="text-sm text-gray-600">Директор: {order.Client.director}</p>}
                         {order.Client.phone && <p className="text-sm text-gray-600">Телефон: {order.Client.phone}</p>}
                         {order.Client.email && <p className="text-sm text-gray-600">Email: <a href={`mailto:${order.Client.email}`} className="text-primary hover:underline">{order.Client.email}</a></p>}
                     </div>
                 ) : (
                    <p className="text-sm text-gray-500">Информация о клиенте не найдена.</p>
                 )}
             </div>

            <div className="bg-white rounded-lg shadow mb-6 p-6">
                <h2 className="text-xl font-bold mb-4">Детали Заказа</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                  <div className="md:col-span-1">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Информация о Заказе</h3>
                      <p className="text-sm text-gray-600">Номер: <span className="font-medium text-gray-800">{order.orderNumber}</span></p>
                      <p className="text-sm text-gray-600">Дата заказа: <span className="font-medium text-gray-800">{formatDate(order.orderDate)}</span></p>
                      <p className="text-sm text-gray-600">Период: <span className="font-medium text-gray-800">{formatDate(order.startDate)} - {formatDate(order.endDate)}</span></p>
                      <div>
                        <p className="text-sm text-gray-500">Статус</p>
                        <span 
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(order.status)}`}
                        >
                          {getStatusText(order.status)}
                        </span>
                      </div>
                      {order.contractId && (
                          <p className="text-sm text-gray-600">Договор: <span className="font-medium text-gray-800">#{order.contractId}</span></p>
                      )}
                       {canEdit && (
                           <div className="mt-2">
                              <label htmlFor="status-update" className="sr-only">Обновить статус</label>
                              <select 
                                  id="status-update"
                                  value={order.status}
                                  onChange={(e) => handleUpdateStatus(e.target.value as keyof typeof ORDER_STATUS_MAP)}
                                  className="form-input-sm"
                                  disabled={updatingStatus}
                              >
                                  {Object.entries(ORDER_STATUS_MAP).map(([key, label]) => (
                                      <option key={key} value={key}>{label}</option>
                                  ))}
                              </select>
                              {updatingStatus && <span className="ml-2 text-xs text-gray-500">Обновление...</span>}
                           </div>
                       )}
                  </div>

                  <div className="md:col-span-1">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Финансы</h3>
                      <div className="space-y-1 text-sm">
                          <div className="flex justify-between"><span className="text-gray-600">Сумма:</span> <span className="font-medium text-gray-800">{formatCurrency(order.totalAmount)}</span></div>
                          <div className="flex justify-between"><span className="text-gray-600">Скидка:</span> <span className="font-medium text-green-600">- {formatCurrency(order.discountAmount)}</span></div>
                          <hr className="my-1" />
                          <div className="flex justify-between text-base font-bold"><span className="text-gray-800">Итого:</span> <span className="text-primary">{formatCurrency(order.finalAmount)}</span></div>
                          <div className="flex justify-between mt-2"><span className="text-gray-600">Оплачено:</span> <span className="font-medium text-blue-600">{formatCurrency(order.paidAmount)}</span></div>
                           <div className="flex justify-between"><span className="text-gray-600">Остаток:</span> <span className="font-medium text-red-600">{formatCurrency(order.finalAmount - order.paidAmount)}</span></div>
                      </div>
                      <div className="mt-3">
                          <div className="relative pt-1">
                              <div className="flex mb-2 items-center justify-between">
                                  <div>
                                      <span className="text-xs font-semibold inline-block py-1 px-2 uppercase rounded-full text-blue-600 bg-blue-200">
                                          Прогресс Оплаты
                                      </span>
                                  </div>
                                  <div className="text-right">
                                      <span className="text-xs font-semibold inline-block text-blue-600">
                                          {formatPaymentProgress()}
                                      </span>
                                  </div>
                              </div>
                              <div className="overflow-hidden h-2 mb-4 text-xs flex rounded bg-blue-200">
                                  <div style={{ width: formatPaymentProgress() }} className="shadow-none flex flex-col text-center text-white justify-center bg-blue-500"></div>
                              </div>
                          </div>
                      </div>
                  </div>
                </div>

                {(order.notes || order.paymentTerms) && (
                  <div className="mt-6 pt-4 border-t border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-6">
                      {order.notes && (
                          <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Заметки по Заказу:</h4>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">{order.notes}</p>
                          </div>
                      )}
                      {order.paymentTerms && (
                          <div>
                              <h4 className="font-semibold text-gray-700 mb-1">Условия Оплаты:</h4>
                              <p className="text-sm text-gray-600 whitespace-pre-wrap">{order.paymentTerms}</p>
                          </div>
                      )}
                  </div>
                )}
            </div>

            <div className="bg-white rounded-lg shadow mb-6 p-6">
                <h2 className="text-xl font-bold mb-4">Позиции Заказа</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Конструкция</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Период</th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Кол-во</th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Цена ед.</th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Скидка</th>
                        <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Итого</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Статус</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Заметки</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {order.OrderItems.length > 0 ? (
                        order.OrderItems.map(item => (
                          <tr key={item.id}>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-gray-900">{item.Structure.code}</div>
                                        <div className="text-xs text-gray-500">{item.Structure.type} - {item.Structure.location}</div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-600">
                                        {formatDate(item.startDate)} - {formatDate(item.endDate)}
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-600 text-right">
                                      <span className="inline-flex items-center text-xs">
                                        <CalendarDaysIcon className="h-3 w-3 mr-1 text-gray-400" />
                                        {formatDuration(item.startDate, item.endDate, item.isMonthlyRate, item.quantity)}
                                      </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-600 text-right">
                                        {formatCurrency(item.unitPrice)}
                                        <span className="ml-1 inline-flex items-center text-xs text-gray-500" title={item.isMonthlyRate ? 'Цена за месяц' : 'Цена за день'}>
                                            {item.isMonthlyRate === true ? (
                                              '/мес'
                                            ) : item.isMonthlyRate === null ? (
                                              '/eд'
                                            ) : (
                                              '/день'
                                            )}
                                             <InformationCircleIcon className="h-3 w-3 ml-0.5 text-gray-400"/>
                                        </span>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-green-600 text-right">
                                        {item.discountPercentage !== null && item.discountPercentage > 0 ? 
                                            `${item.discountPercentage}% (${formatCurrency(item.totalPrice - item.finalPrice)})` 
                                            : '-'}
                                    </td>
                                    <td className="px-4 py-4 text-sm font-medium text-gray-900 text-right">
                                        {formatCurrency(item.finalPrice)}
                                    </td>
                                     <td className="px-4 py-4 text-xs">
                                        {item.status || '-'}
                                     </td>
                                    <td className="px-4 py-4 text-xs text-gray-500">
                                        {item.notes || '-'}
                                    </td>
                                </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="px-4 py-4 text-center text-sm text-gray-500">
                            Нет позиций в этом заказе.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
            </div>

            {order.Payments && order.Payments.length > 0 && (
               <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-xl font-bold mb-4">История Оплат</h2>
                  <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                              <tr>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Дата</th>
                                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Сумма</th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Метод</th>
                                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Референс</th>
                              </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                              {order.Payments.map(payment => (
                                  <tr key={payment.id}>
                                      <td className="px-4 py-4 text-sm text-gray-600">{formatDate(payment.date)}</td>
                                      <td className="px-4 py-4 text-sm text-gray-900 font-medium text-right">{formatCurrency(payment.amount)}</td>
                                      <td className="px-4 py-4 text-sm text-gray-600">{payment.method}</td>
                                      <td className="px-4 py-4 text-sm text-gray-600">{payment.referenceNumber || '-'}</td>
                                  </tr>
                              ))}
                          </tbody>
                      </table>
                  </div>
               </div>
            )}
        </div>
        <div className="lg:col-span-1 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-4">Создатель Заказа</h2>
                <div className="space-y-1 text-sm">
                    <p className="text-sm text-gray-500">Создал</p>
                    <p className="font-medium flex items-center">
                      <UserIcon className="h-4 w-4 mr-1 text-gray-400"/> 
                      {order.CreatedBy 
                        ? `${order.CreatedBy.firstName || ''} ${order.CreatedBy.lastName || ''}`.trim() || order.CreatedBy.username 
                        : 'Неизвестно'}
                    </p>
                </div>
            </div>
        </div>
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Удалить заказ?"
      >
        <div className="mt-2">
          <p className="text-sm text-gray-500">
            Вы уверены, что хотите удалить заказ #{order.orderNumber}? Это действие необратимо.
          </p>
        </div>
        <div className="mt-4 flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={() => setShowDeleteModal(false)}>
            Отмена
          </button>
          <button type="button" className="btn-danger" onClick={handleDelete}>
            Удалить
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default OrderDetails;
